import React, { useState, useEffect } from 'react';
import { FaFilePdf, FaDownload, FaFolder, FaCalendarAlt, FaFileAlt, FaImage, FaEye, FaTimes } from 'react-icons/fa';

const MateriMitra = () => {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [previewModal, setPreviewModal] = useState({ isOpen: false, material: null });

  // Load materials from JSON file
  const loadMaterials = async () => {
    try {
      const response = await fetch('/materials/materials.json');
      if (response.ok) {
        const data = await response.json();
        setMaterials(data);
      } else {
        setMaterials([]);
      }
    } catch (err) {
      console.error('Error loading materials:', err);
      setError('Gagal memuat daftar materi');
      setMaterials([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMaterials();
  }, []);

  // Filter materials based on search term
  const filteredMaterials = materials.filter(material =>
    material.originalName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Get file type
  const getFileType = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase();
    if (['pdf'].includes(extension)) return 'pdf';
    if (['jpg', 'jpeg', 'png'].includes(extension)) return 'image';
    return 'other';
  };

  // Get file icon
  const getFileIcon = (fileName) => {
    const type = getFileType(fileName);
    if (type === 'pdf') return <FaFilePdf className="h-8 w-8 text-red-500" />;
    if (type === 'image') return <FaImage className="h-8 w-8 text-green-500" />;
    return <FaFileAlt className="h-8 w-8 text-gray-500" />;
  };


  // Open preview modal
  const openPreview = (material) => {
    setPreviewModal({ isOpen: true, material });
  };

  // Close preview modal
  const closePreview = () => {
    setPreviewModal({ isOpen: false, material: null });
  };

  // Handle download
  const handleDownload = (material) => {
    const link = document.createElement('a');
    link.href = `/materials/${material.fileName}`;
    link.download = material.originalName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2 flex items-center gap-3">
          <FaFileAlt className="text-amber-500" />
          Materi Mitra
        </h1>
        <p className="text-gray-600">Akses dan download materi yang tersedia untuk mitra</p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}



      {/* Search Bar */}
      <div className="bg-white p-4 rounded-lg shadow-sm mb-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex-1 relative max-w-md">
            <input
              type="text"
              placeholder="Cari materi..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-4 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            />
          </div>
          <div className="text-sm text-gray-600">
            {searchTerm ? `${filteredMaterials.length} dari ${materials.length} materi` : `${materials.length} materi tersedia`}
          </div>
        </div>
      </div>

      {/* Materials Grid */}
      {filteredMaterials.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm text-center py-12">
          <FaFolder className="mx-auto h-16 w-16 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {searchTerm ? 'Tidak ada materi yang ditemukan' : 'Belum ada materi tersedia'}
          </h3>
          <p className="text-gray-500">
            {searchTerm 
              ? 'Coba gunakan kata kunci yang berbeda' 
              : 'Tunggu admin mengupload materi untuk mitra'
            }
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredMaterials.map((material) => (
            <div key={material.id} className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow overflow-hidden">
              {/* Card Header */}
              <div className="p-4 border-b border-gray-200">
                <div className="flex items-center gap-3 mb-2">
                  {getFileIcon(material.originalName)}
                  <h3 className="font-medium text-gray-900 truncate flex-1">
                    {material.originalName}
                  </h3>
                </div>
                <div className="flex items-center text-xs text-gray-500 gap-3">
                  <span className="flex items-center gap-1">
                    <FaCalendarAlt />
                    {formatDate(material.uploadDate)}
                  </span>
                  <span>{formatFileSize(material.size)}</span>
                </div>
              </div>

              {/* Preview Section */}
              <div className="h-48 bg-gray-50 relative overflow-hidden">
                {getFileType(material.originalName) === 'pdf' ? (
                  <div className="h-full flex items-center justify-center">
                    <div className="text-center">
                      <FaFilePdf className="h-16 w-16 text-red-500 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">Dokumen PDF</p>
                    </div>
                  </div>
                ) : getFileType(material.originalName) === 'image' ? (
                  <div className="h-full flex items-center justify-center">
                    <div className="text-center">
                      <FaImage className="h-16 w-16 text-green-500 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">Gambar</p>
                    </div>
                  </div>
                ) : (
                  <div className="h-full flex items-center justify-center">
                    <div className="text-center">
                      <FaFileAlt className="h-16 w-16 text-gray-500 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">File</p>
                    </div>
                  </div>
                )}
                
                {/* Preview overlay */}
                <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center opacity-0 hover:opacity-100">
                  <button
                    onClick={() => openPreview(material)}
                    className="bg-white text-gray-700 px-3 py-1 rounded-lg text-sm font-medium flex items-center gap-2 shadow-lg"
                  >
                    <FaEye />
                    Preview
                  </button>
                </div>
              </div>

              {/* Card Footer */}
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">
                    {material.uploadedBy && `Diupload oleh: ${material.uploadedBy}`}
                  </span>
                  <button
                    onClick={() => handleDownload(material)}
                    className="flex items-center gap-1 bg-amber-500 hover:bg-amber-600 text-white px-3 py-1 rounded text-sm font-medium transition-colors"
                  >
                    <FaDownload className="text-xs" />
                    Download
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Preview Modal */}
      {previewModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Preview: {previewModal.material?.originalName}
              </h3>
              <button
                onClick={closePreview}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FaTimes className="h-6 w-6" />
              </button>
            </div>
            <div className="p-4 h-96 overflow-hidden">
              {getFileType(previewModal.material?.originalName) === 'pdf' ? (
                <div className="h-full flex items-center justify-center bg-gray-50 rounded">
                  <div className="text-center">
                    <FaFilePdf className="h-24 w-24 text-red-500 mx-auto mb-4" />
                    <p className="text-lg font-medium text-gray-900 mb-2">Dokumen PDF</p>
                    <p className="text-gray-600 mb-4">
                      Preview PDF tidak tersedia. Silakan download untuk melihat isi dokumen.
                    </p>
                    <button
                      onClick={() => handleDownload(previewModal.material)}
                      className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 mx-auto"
                    >
                      <FaDownload />
                      Download PDF
                    </button>
                  </div>
                </div>
              ) : getFileType(previewModal.material?.originalName) === 'image' ? (
                <div className="h-full flex items-center justify-center bg-gray-50 rounded">
                  <div className="text-center">
                    <FaImage className="h-24 w-24 text-green-500 mx-auto mb-4" />
                    <p className="text-lg font-medium text-gray-900 mb-2">Gambar</p>
                    <p className="text-gray-600 mb-4">
                      Preview gambar akan ditampilkan di sini setelah diupload ke server.
                    </p>
                    <button
                      onClick={() => handleDownload(previewModal.material)}
                      className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 mx-auto"
                    >
                      <FaDownload />
                      Download Gambar
                    </button>
                  </div>
                </div>
              ) : (
                <div className="h-full flex items-center justify-center bg-gray-50 rounded">
                  <div className="text-center">
                    <FaFileAlt className="h-24 w-24 text-gray-500 mx-auto mb-4" />
                    <p className="text-lg font-medium text-gray-900 mb-2">File</p>
                    <p className="text-gray-600">Preview tidak tersedia untuk tipe file ini.</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Info Card */}
      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <FaFileAlt className="h-6 w-6 text-blue-600" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">
              Informasi Materi
            </h3>
            <div className="mt-2 text-sm text-blue-700">
              <ul className="list-disc list-inside space-y-1">
                <li>Format yang tersedia: PDF, JPG, JPEG, dan PNG</li>
                <li>Klik "Preview" untuk melihat preview file</li>
                <li>Klik "Download" untuk mengunduh file</li>
                <li>Gunakan fitur pencarian untuk menemukan materi tertentu</li>
                <li>Materi dikelola oleh admin NH Press</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MateriMitra;
